<template>
  <ClientOnly>
    <UBreadcrumb
      v-if="
        typeof route.name === 'string'
          && !breadcrumb?.ignoredRoutes?.includes(route.name.split('___')[0])
      "
      :ui="{ active: 'text-brand-600 dark:text-brand-400' }"
      class="mb-3"
      :links="items"
    />
    <template #fallback>
      <slot name="skeleton">
        <div class="flex items-center relative min-w-0 mb-3 space-x-2 h-[24px]">
          <USkeleton
            v-for="(i, index) in items"
            :key="index"
            class="h-[20px] w-[70px]"
          />
        </div>
      </slot>
    </template>
  </ClientOnly>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'

const route = useRoute()
const breadcrumb = useRuntimeConfig().public.breadcrumb
const { defaultLocale, locale, t } = useI18n()

type BreadcrumbItem = {
  label: string
  to: RouteLocationRaw | string
}

const domain = useRequestURL().origin

const items = computed<BreadcrumbItem[]>(() => {
  const paths = route.path.split('/').filter(Boolean)
  const adjustedPaths = paths[0] === locale.value ? paths.slice(1) : paths
  const basePath
    = domain
      + (locale.value && locale.value !== defaultLocale ? `/${locale.value}` : '')

  const baseItems = [
    {
      label: t('breadcrumb.home'),
      to: `${basePath || '/'}`,
    },
  ]

  const breadcrumbItems = adjustedPaths
    .map((path, index) => {
      const isLastItem = index === adjustedPaths.length - 1
      const breadcrumbMeta = route.meta.breadcrumb

      return {
        label:
          isLastItem && breadcrumbMeta?.name
            ? breadcrumbMeta.name
            : path.charAt(0).toUpperCase() + path.slice(1).replaceAll('-', ' '),
        to: `${basePath}/${adjustedPaths.slice(0, index + 1).join('/')}`,
      }
    })
    .filter(
      (_, index) => !route.meta.breadcrumb?.removeIndexes?.includes(index + 1),
    )

  return [...baseItems, ...breadcrumbItems]
})

useSchemaOrg([
  {
    '@type': 'BreadcrumbList',
    'itemListElement': items.value.map((item, index) => ({
      '@type': 'ListItem',
      'item': item.to,
      'name': item.label,
      'position': index + 1,
    })),
  },
])
</script>

<style></style>
